import { TextField, Box, Typography, useTheme } from "@mui/material";
import { RegisterOptions, useFormContext } from "react-hook-form";
import type {
  OutlinedTextFieldProps as OutlinedProps,
  FilledTextFieldProps as FilledProps,
  StandardTextFieldProps as StandardProps,
} from "@mui/material";
import React from "react";

type Variant = "standard" | "filled" | "outlined";
type VariantPropsMap<T extends Variant> = {
  standard: StandardProps;
  filled: FilledProps;
  outlined: OutlinedProps;
}[T];

type HookFormTextFieldProps<T extends Variant> = VariantPropsMap<T> & {
  name: string;
  label?: string | React.ReactNode;
  options?: RegisterOptions;
};

export function HookFormTextField<T extends Variant>({
  variant = "outlined", // default to outlined
  label,
  size = "small",
  ...props
}: HookFormTextFieldProps<T>) {
  // using the theme from MUI
  const theme = useTheme();
  // extracting the register and formState from the react-hook-form context
  const { register, formState, unregister } = useFormContext();
  const { errors } = formState;

  React.useEffect(() => {
    // unregister the field when the component unmounts
    return () => unregister(props.name);
  }, []);

  return (
    <Box>
      {/* Input field label */}
      {label ? (
        <Box py={"2px"} pl={"2px"}>
          <InputLabel>{label}</InputLabel>
        </Box>
      ) : null}

      {/* Actual Input field */}
      <TextField
        fullWidth
        size={size}
        variant={variant}
        {...props}
        {...register(props.name, props?.options)}
      />

      {/* Input field Error Message */}
      <Box
        height={errors[props.name] ? "16px" : 0}
        width={"100%"}
        py={"2px"}
        pl={"2px"}
        sx={{ transition: "height 0.2s ease-in-out" }}
      >
        {errors[props.name]?.message ? (
          <InputError>{`${errors[props.name]?.message}`}</InputError>
        ) : null}
      </Box>
    </Box>
  );
}

export function InputLabel(props: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color={"text.secondary"} fontWeight={500}>
      {props.children}
    </Typography>
  );
}

export function InputError(props: { children: React.ReactNode }) {
  const theme = useTheme();

  return (
    <Box
      fontSize={"0.75rem"}
      lineHeight={1}
      color={theme.palette.error.main}
      fontWeight={500}
    >
      {props.children}
    </Box>
  );
}
